import React, { useState } from 'react';
import { MapPin, Phone, Mail, Send, MessageCircle } from 'lucide-react';
import { contactData, companyInfo } from '../data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    // TODO: Implement EmailJS integration for sending emails
    // For now, temporarily disabled - will be replaced with EmailJS
    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitted(true);
      setFormData({
        company: '',
        name: '',
        email: '',
        phone: '',
        message: '',
      });
      // Reset submitted state after 5 seconds
      setTimeout(() => setSubmitted(false), 5000);
    } catch (err) {
      setError('Mesaj gönderilemedi. Lütfen tekrar deneyin.');
      console.error('Contact form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-black">
      <div className="container mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div className="max-w-4xl mb-16 md:mb-20">
          <span className="text-gray-500 text-sm uppercase tracking-widest font-medium mb-4 block">
            {contactData.subtitle}
          </span>
          <h2 className="text-white text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase">
            {contactData.title}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Info */}
          <div>
            <div className="space-y-8 mb-12">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white text-black flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2">Adres</h3>
                  <p className="text-gray-500">
                    {contactData.address.line1}<br />
                    {contactData.address.line2}<br />
                    {contactData.address.country}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white text-black flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2">Telefon</h3>
                  <a 
                    href={`tel:${contactData.phone.replace(/\s/g, '')}`}
                    className="text-gray-500 hover:text-white transition-colors"
                  >
                    {contactData.phone}
                  </a>
                </div>
              </div>

              {/* WhatsApp */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-500 text-white flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2">WhatsApp</h3>
                  <a 
                    href={contactData.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-green-500 transition-colors"
                  >
                    WhatsApp ile İletişime Geçin
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white text-black flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-white font-bold uppercase tracking-wide mb-2">E-posta</h3>
                  <a 
                    href={`mailto:${contactData.email}`}
                    className="text-gray-500 hover:text-white transition-colors"
                  >
                    {contactData.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="aspect-video border border-gray-800 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d12345.67890!2d26.3333!3d39.7667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14b0a0e5e5e5e5e5%3A0x0!2zw5x2ZWNpaywgRXppbmUsIMOHYW5ha2thbGU!5e0!3m2!1str!2str!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="AR Tarım Konum"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 md:p-12">
            <h3 className="text-black text-2xl font-bold uppercase tracking-wide mb-8">
              Bize Ulaşın
            </h3>

            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-black text-white flex items-center justify-center mx-auto mb-6">
                  <Send className="w-8 h-8" />
                </div>
                <h4 className="text-black text-xl font-bold mb-2">Mesajınız Alındı</h4>
                <p className="text-gray-600">En kısa sürede sizinle iletişime geçeceğiz.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 text-sm">
                    {error}
                  </div>
                )}

                {/* Company */}
                <div>
                  <label htmlFor="company" className="block text-gray-600 text-sm uppercase tracking-wider mb-2">
                    Firma Adı *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-gray-300 text-black px-4 py-3 focus:border-black focus:outline-none transition-colors"
                  />
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-gray-600 text-sm uppercase tracking-wider mb-2">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-gray-300 text-black px-4 py-3 focus:border-black focus:outline-none transition-colors"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-gray-600 text-sm uppercase tracking-wider mb-2">
                    E-posta *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-transparent border border-gray-300 text-black px-4 py-3 focus:border-black focus:outline-none transition-colors"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-gray-600 text-sm uppercase tracking-wider mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-transparent border border-gray-300 text-black px-4 py-3 focus:border-black focus:outline-none transition-colors"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-gray-600 text-sm uppercase tracking-wider mb-2">
                    Mesajınız *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full bg-transparent border border-gray-300 text-black px-4 py-3 focus:border-black focus:outline-none transition-colors resize-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white py-4 font-bold uppercase tracking-widest hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    'Gönderiliyor...'
                  ) : (
                    <>
                      Mesaj Gönder
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
